import { useEffect, useState } from 'react';
import { Send, Phone, User, Mail, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { WhatsAppIcon } from '@/components/property/ContactIcons';
import { toast } from 'sonner';
import { leadApi } from '@/services/api';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface MobileLeadFormSectionProps {
  listingId: string;
  listingLabel?: string;
  referenceCode?: string;
  listingKind?: 'property' | 'plot' | 'project';
  property?: any;
}

const ROLE_LABELS: Record<string, string> = {
  buyer: 'Buyer / Tenant',
  agent: 'Agent',
  other: 'Other',
};

export function MobileLeadFormSection({
  listingId,
  listingLabel = 'property',
  referenceCode,
  listingKind = 'property',
  property,
}: MobileLeadFormSectionProps) {
  const { user, profile } = useAuth();
  const refId = (referenceCode || `ID${listingId.slice(0, 8)}`).toUpperCase();
  const [role, setRole] = useState('buyer');
  const roleIntro: Record<string, string> = {
    buyer: 'I am a Buyer / Tenant',
    agent: 'I am an Agent representing a client',
    other: 'I am reaching out',
  };
  const defaultMsg = `Hello, ${roleIntro[role] || roleIntro.buyer} and I would like to inquire about your ${listingLabel} (EthioRealEstates - ${refId}). Please share availability, pricing and next steps. I would also like to schedule a viewing at your earliest convenience. Thank you.`;
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: defaultMsg });
  const [keepInformed, setKeepInformed] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [msgEdited, setMsgEdited] = useState(false);

  // Keep the prewritten message in sync (with role) until the user edits it
  useEffect(() => {
    if (msgEdited) return;
    setForm(f => ({ ...f, message: defaultMsg }));
  }, [defaultMsg, msgEdited]);

  // Auto-fill from logged-in user profile (falls back to auth metadata)
  useEffect(() => {
    if (!user) return;
    const meta: any = (user as any)?.user_metadata || {};
    setForm(f => ({
      ...f,
      name: f.name || (profile as any)?.full_name || (profile as any)?.name || meta.full_name || meta.name || '',
      phone: f.phone || (profile as any)?.phone || meta.phone || '',
      email: f.email || user.email || meta.email || '',
    }));
  }, [user, profile]);

  const [ownerInfo, setOwnerInfo] = useState<any>(null);
  useEffect(() => {
    const ownerId = property?.user_id;
    if (!ownerId) return;
    (async () => {
      const { data: agent } = await supabase
        .from('agent_profiles')
        .select('photo_url, phone_number, agency_id, agencies(name, logo_url, phone, slug)')
        .eq('user_id', ownerId)
        .maybeSingle();
      if (agent) { setOwnerInfo({ kind: 'agent', ...agent }); return; }
      const { data: dev } = await supabase
        .from('developer_profiles')
        .select('logo_url, phone, company_name, slug')
        .eq('user_id', ownerId)
        .maybeSingle();
      if (dev) setOwnerInfo({ kind: 'developer', ...dev });
    })();
  }, [property?.user_id]);

  const agency = ownerInfo?.agencies;
  const contactPhone =
    property?.contact_phone ||
    ownerInfo?.phone_number ||
    ownerInfo?.phone ||
    agency?.phone ||
    '+251911000000';
  const agentName = property?.contact_person || 'Property Agent';
  const agencyName =
    agency?.name ||
    ownerInfo?.company_name ||
    property?.agency_name ||
    property?.developer_name ||
    agentName;
  const displayLogo =
    agency?.logo_url || ownerInfo?.logo_url || property?.agency_logo_url || property?.logo_url || null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Please fill in your name and phone number');
      return;
    }
    setSubmitting(true);
    try {
      const roleLabel = ROLE_LABELS[role] || 'Buyer / Tenant';
      const userMsg = form.message.trim();
      const composedMessage = `[Contact type: ${roleLabel}]${userMsg ? `\n\n${userMsg}` : ''}`;
      await leadApi.create({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        message: composedMessage,
        ...(listingKind === 'plot'
          ? { plot_id: listingId }
          : listingKind === 'project'
          ? { project_id: listingId }
          : { property_id: listingId }),
        source: 'website',
      });
      toast.success('Your inquiry has been sent!');
      setForm({
        name: user ? ((profile as any)?.full_name || (profile as any)?.name || '') : '',
        phone: user ? ((profile as any)?.phone || '') : '',
        email: user ? (user.email || '') : '',
        message: defaultMsg,
      });
      setMsgEdited(false);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send inquiry. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <section id="lead-form" className="scroll-mt-[120px]">
      <Card className="border border-border/60 shadow-sm">
        <CardContent className="p-4 sm:p-5">
          {/* Header */}
          <div className="flex flex-col items-center text-center pb-4 mb-4 border-b">
            {displayLogo ? (
              <img src={displayLogo} alt={agencyName} className="h-16 w-auto max-w-[140px] object-contain mb-2" />
            ) : (
              <div className="h-16 w-16 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-bold text-2xl mb-2">
                {agencyName.charAt(0)}
              </div>
            )}
            <div className="font-bold text-base leading-tight">{agencyName}</div>
            {agentName && (
              <div className="text-sm font-semibold text-foreground/80 mt-0.5">{agentName}</div>
            )}
            <div className="text-xs text-muted-foreground mt-0.5">EthioRealEstates · {refId}</div>
          </div>

          {/* Call + WhatsApp stacked */}
          <Button
            asChild
            className="w-full h-11 mb-2 bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-sm gap-2 rounded-md"
          >
            <a href={`tel:${contactPhone}`}>
              <Phone className="h-4 w-4" /> Call
            </a>
          </Button>
          <Button
            asChild
            variant="outline"
            className="w-full h-11 mb-5 font-semibold text-sm border-border hover:bg-muted gap-2 rounded-md"
          >
            <a
              href={`https://wa.me/${contactPhone.replace(/[^0-9]/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <WhatsAppIcon className="h-4 w-4" />
              WhatsApp
            </a>
          </Button>

          <h3 className="font-bold text-base mb-3">Send a Message</h3>

          <form onSubmit={handleSubmit} className="space-y-3.5">
            <div className="relative">
              <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-muted-foreground/70" strokeWidth={1.75} />
              <Input
                required
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Your Name *"
                className="pl-10 h-12 text-sm font-normal placeholder:text-muted-foreground/60 placeholder:font-normal"
              />
            </div>

            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-muted-foreground/70" strokeWidth={1.75} />
              <Input
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="Email Address *"
                className="pl-10 h-12 text-sm font-normal placeholder:text-muted-foreground/60 placeholder:font-normal"
              />
            </div>

            <div className="relative">
              <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-muted-foreground/70" strokeWidth={1.75} />
              <Input
                type="tel"
                required
                value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="Phone Number"
                className="pl-10 h-12 text-sm font-normal placeholder:text-muted-foreground/60 placeholder:font-normal"
              />
            </div>

            <Textarea
              rows={3}
              value={form.message}
              onChange={e => { setForm(f => ({ ...f, message: e.target.value })); setMsgEdited(true); }}
              placeholder="How can we help you?"
              className="resize-none text-sm font-normal placeholder:text-muted-foreground/60 placeholder:font-normal py-3 px-3.5"
            />

            <div className="space-y-2 pt-1">
              <Label className="text-xs font-semibold">I am a <span className="text-destructive">*</span></Label>
              <RadioGroup value={role} onValueChange={setRole} className="flex gap-4 flex-wrap">
                {Object.entries(ROLE_LABELS).map(([value, label]) => (
                  <div key={value} className="flex items-center gap-1.5">
                    <RadioGroupItem value={value} id={`mlf-role-${value}`} />
                    <Label htmlFor={`mlf-role-${value}`} className="text-xs cursor-pointer">
                      {label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="mlf-keep-informed"
                checked={keepInformed}
                onCheckedChange={c => setKeepInformed(!!c)}
              />
              <Label htmlFor="mlf-keep-informed" className="text-xs cursor-pointer">
                Keep me informed about similar properties
              </Label>
            </div>

            <Button type="submit" className="w-full h-11 font-bold gap-2" disabled={submitting}>
              <Send className="h-4 w-4" />
              {submitting ? 'Sending...' : 'Send Message'}
            </Button>

            <p className="text-[11px] text-muted-foreground text-center flex items-center justify-center gap-1 pt-1">
              <Lock className="h-3 w-3" />
              Your information is secure and will not be shared.
            </p>
          </form>
        </CardContent>
      </Card>
    </section>
  );
}
